// @ts-check
import React, { useCallback } from 'react';
import { Formik } from 'formik';
import { decamelizeKeys } from 'humps';
import { FormStatusErrors } from 'preferral-ui/components/formik/FormStatusErrors';
import { TextField } from 'preferral-ui/components/formik/TextField';
import { useMutation } from 'react-query';
import api from 'utils/api';
import * as Yup from 'yup';
import { Button } from 'preferral-ui/components/Button';
import { ColorSwatchField } from 'preferral-ui/components/formik/ColorSwatchField';

const hexRegex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** @typedef {{
  label: string;
  description: string;
  color: string;
}} FormValues */

const validationSchema = Yup.object()
  .shape({
    label: Yup.string().required('Required'),
    description: Yup.string(),
    color: Yup.string().required('Required').matches(hexRegex, "Invalid color"),
  })
  .required();

const initialValues = {
  label: '',
  description: '',
  color: '',
};

/** @typedef {{
  organizationId: string;
  onSuccess(): void;
}} NewReferralTagFormProps */

/** @type { React.FC<NewReferralTagFormProps> } */
export const NewReferralTagForm = (props) => {
  const { organizationId, onSuccess } = props;

  const mutation = useMutation((/** @type { FormValues } */ exportInput) =>
    api
      .post(`/agent_portal/api/organizations/${organizationId}/referral_tags`, {
        referral_tag: decamelizeKeys(exportInput),
      })
      .then((resp) => resp.data)
  );

  const onSubmit = useCallback(
    (values, formikHelpers) => {
      const { setStatus, setSubmitting } = formikHelpers;

      setStatus({ errors: null });

      return mutation.mutateAsync(values).then(
        (_data) => onSuccess(),
        (err) => {
          setSubmitting(false);
          if (err && err.errors && err.errors._error) {
            setStatus({
              errors: err.errors._error.map((e) => ({ key: '', message: e })),
            });
          } else {
            return Promise.reject(err);
          }
        }
      );
    },
    [onSuccess, mutation]
  );

  return (
    <div className="_NewReferralTagForm">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, isSubmitting, status }) => (
          <form onSubmit={handleSubmit}>
            <FormStatusErrors status={status} />
            <div className="grid grid-cols-2 gap-4">
              <TextField name="label" label="Tag Label" />

              {/* <ColorSwatchPicker value={color} onChange={setColor} /> */}
              <ColorSwatchField name="color" label="Color" />
            </div>


            <div className="mt-4">
              <TextField name="description" label="Tag Description" indicateOptional />
            </div>

            <div className="mt-6 text-center">
              <Button
                type="submit"
                kind="primary"
                color="blue"
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                Create Tag
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};
